'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  createBusinessReportCenter,
  createBusinessReportCenterAlias,
  getBusinessReportCenterOptions,
  getBusinessReportCenters,
  setBusinessReportCenterActive,
  setBusinessReportCenterAliasActive,
  updateBusinessReportCenter,
  updateBusinessReportCenterAlias,
  type BusinessReportAccessCenterOption,
  type BusinessReportCenter,
  type BusinessReportCenterAlias,
  type BusinessReportCenterAliasInput,
  type BusinessReportCenterInput,
  type BusinessReportCenterSourceOption,
} from '../lib/api/business-reports';
import { AdminAlertModal, AdminConfirmModal, Loader } from './common';

type CenterForm = {
  centerCode: string;
  displayName: string;
  accessCenterName: string;
  displayOrder: string;
  launchDate: string;
  effectiveFrom: string;
  effectiveTo: string;
};

type AliasForm = {
  sourceNamespace: string;
  rawCenterValue: string;
  dataSourceId: string;
  mappingOwner: string;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
};

type Confirmation =
  | { kind: 'center'; center: BusinessReportCenter; activate: boolean }
  | { kind: 'alias'; center: BusinessReportCenter; alias: BusinessReportCenterAlias; activate: boolean };

const EMPTY_CENTER: CenterForm = {
  centerCode: '',
  displayName: '',
  accessCenterName: '',
  displayOrder: '100',
  launchDate: '',
  effectiveFrom: '',
  effectiveTo: '',
};

const EMPTY_ALIAS: AliasForm = {
  sourceNamespace: 'LOOKER',
  rawCenterValue: '',
  dataSourceId: '',
  mappingOwner: '',
  effectiveFrom: '',
  effectiveTo: '',
  isActive: true,
};

export function BusinessReportCentersTab({ active, token, onTopBarActionsChange }: { active: boolean; token?: string; onTopBarActionsChange?: (actions: ReactNode) => void }) {
  const [centers, setCenters] = useState<BusinessReportCenter[]>([]);
  const [accessOptions, setAccessOptions] = useState<BusinessReportAccessCenterOption[]>([]);
  const [sourceOptions, setSourceOptions] = useState<BusinessReportCenterSourceOption[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [centerForm, setCenterForm] = useState<CenterForm | null>(null);
  const [editingCenterCode, setEditingCenterCode] = useState<string | null>(null);
  const [aliasForm, setAliasForm] = useState<AliasForm | null>(null);
  const [aliasCenterCode, setAliasCenterCode] = useState<string | null>(null);
  const [editingAliasId, setEditingAliasId] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, [accessRows, sourceRows]] = await Promise.all([
        getBusinessReportCenters(token, { search, status }),
        getBusinessReportCenterOptions(token),
      ]);
      setCenters(rows);
      setAccessOptions(accessRows.filter((option) => option.name.trim().toLowerCase() !== 'management'));
      setSourceOptions(sourceRows);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load Centre Master', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [search, status, token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const stats = useMemo(() => ({
    total: centers.length,
    active: centers.filter((center) => center.isActive).length,
    aliases: centers.reduce((total, center) => total + center.aliases.length, 0),
    missingLaunchDate: centers.filter((center) => !center.launchDate).length,
  }), [centers]);

  const openCreate = useCallback(() => {
    setEditingCenterCode(null);
    setCenterForm({
      ...EMPTY_CENTER,
      accessCenterName: accessOptions[0]?.name || '',
      displayOrder: String(Math.max(0, ...centers.map((center) => center.displayOrder)) + 1),
    });
  }, [accessOptions, centers]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(<button className="btn btn-primary compact-btn" onClick={openCreate}>+ Add Centre</button>);
    return () => onTopBarActionsChange(null);
  }, [active, onTopBarActionsChange, openCreate]);

  if (!active) return null;

  const openEdit = (center: BusinessReportCenter) => {
    setEditingCenterCode(center.centerCode);
    setCenterForm({
      centerCode: center.centerCode,
      displayName: center.displayName,
      accessCenterName: center.accessCenterName,
      displayOrder: String(center.displayOrder),
      launchDate: dateOnly(center.launchDate),
      effectiveFrom: dateOnly(center.effectiveFrom),
      effectiveTo: dateOnly(center.effectiveTo),
    });
  };

  const saveCenter = async () => {
    if (!centerForm) return;
    setLoading(true);
    try {
      const input = centerInput(centerForm);
      if (editingCenterCode) await updateBusinessReportCenter(editingCenterCode, input, token);
      else await createBusinessReportCenter(input, token);
      const wasEditing = Boolean(editingCenterCode);
      setCenterForm(null);
      setEditingCenterCode(null);
      await load();
      setAlert({
        type: 'success',
        title: wasEditing ? 'Centre updated' : 'Centre created',
        message: wasEditing
          ? 'Centre Master changes were normalized and audited.'
          : 'The centre was created inactive. Activate it after reviewing its mapping.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save centre', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const openCreateAlias = (center: BusinessReportCenter) => {
    setAliasCenterCode(center.centerCode);
    setEditingAliasId(null);
    setAliasForm({
      ...EMPTY_ALIAS,
      rawCenterValue: center.displayName,
      mappingOwner: 'Business Reports',
    });
  };

  const openEditAlias = (center: BusinessReportCenter, alias: BusinessReportCenterAlias) => {
    setAliasCenterCode(center.centerCode);
    setEditingAliasId(alias.id);
    setAliasForm({
      sourceNamespace: alias.sourceNamespace,
      rawCenterValue: alias.rawCenterValue,
      dataSourceId: alias.dataSourceId || '',
      mappingOwner: alias.mappingOwner || '',
      effectiveFrom: dateOnly(alias.effectiveFrom),
      effectiveTo: dateOnly(alias.effectiveTo),
      isActive: alias.isActive,
    });
  };

  const saveAlias = async () => {
    if (!aliasForm || !aliasCenterCode) return;
    setLoading(true);
    try {
      const input = aliasInput(aliasForm);
      if (editingAliasId) {
        await updateBusinessReportCenterAlias(aliasCenterCode, editingAliasId, input, token);
      } else {
        await createBusinessReportCenterAlias(aliasCenterCode, input, token);
      }
      const wasEditing = Boolean(editingAliasId);
      setAliasForm(null);
      setAliasCenterCode(null);
      setEditingAliasId(null);
      await load();
      setExpandedCode(aliasCenterCode);
      setAlert({
        type: 'success',
        title: wasEditing ? 'Alias updated' : 'Alias added',
        message: 'Whitespace was normalized; the source value maps to one canonical centre.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save alias', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!confirmation) return;
    const current = confirmation;
    setConfirmation(null);
    setLoading(true);
    try {
      if (current.kind === 'center') {
        await setBusinessReportCenterActive(current.center.centerCode, current.activate, token);
      } else {
        await setBusinessReportCenterAliasActive(
          current.center.centerCode,
          current.alias.id,
          current.activate,
          token,
        );
      }
      await load();
      setAlert({
        type: 'success',
        title: current.activate ? 'Activated' : 'Deactivated',
        message: current.kind === 'center'
          ? `${current.center.displayName} is now ${current.activate ? 'active' : 'inactive'}.`
          : `The ${current.alias.sourceNamespace} alias is now ${current.activate ? 'active' : 'inactive'}.`,
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Status change failed', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="tab-content business-source-page" id="tab-business-report-centers">
      {loading && <Loader message="Processing Centre Master configuration..." />}

      <div className="business-source-kpis">
        <Kpi label="Clinical centres" value={stats.total} tone="neutral" />
        <Kpi label="Active" value={stats.active} tone="success" />
        <Kpi label="Source aliases" value={stats.aliases} tone="info" />
        <Kpi label="Launch date pending" value={stats.missingLaunchDate} tone="neutral" />
      </div>

      <div className="card enterprise-filter-card">
        <div className="enterprise-filter-grid business-center-filter-grid">
        <div className="enterprise-field">
          <span>Search</span>
          <input
            id="business-center-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Centre name, code, access mapping or alias"
          />
        </div>
        <div className="enterprise-field">
          <span>Status</span>
          <select id="business-center-status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="ALL">All centres</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>
        <button className="btn btn-primary compact-btn filter-action" onClick={() => void load()}>Apply Filters</button>
        </div>
      </div>

      <div className="enterprise-card business-source-table-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Canonical Clinical Locations</strong>
            <span>Codes and UUIDs are stable; operational details and mappings remain administratively editable.</span>
          </div>
          <span className="business-source-count">{centers.length} shown</span>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table business-source-table">
            <thead>
              <tr>
                <th>Order / Code</th>
                <th>Centre</th>
                <th>Access Mapping</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Aliases</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {centers.map((center) => (
                <CenterRows
                  key={center.id}
                  center={center}
                  expanded={expandedCode === center.centerCode}
                  onToggle={() => setExpandedCode((current) => current === center.centerCode ? null : center.centerCode)}
                  onEdit={() => openEdit(center)}
                  onAddAlias={() => openCreateAlias(center)}
                  onEditAlias={(alias) => openEditAlias(center, alias)}
                  onStatus={(activate) => setConfirmation({ kind: 'center', center, activate })}
                  onAliasStatus={(alias, activate) => setConfirmation({ kind: 'alias', center, alias, activate })}
                />
              ))}
              {!centers.length && (
                <tr><td colSpan={7} className="empty-cell">No clinical centres match the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {centerForm && (
        <CenterModal
          value={centerForm}
          editing={Boolean(editingCenterCode)}
          accessOptions={accessOptions}
          onChange={setCenterForm}
          onSave={() => void saveCenter()}
          onClose={() => { setCenterForm(null); setEditingCenterCode(null); }}
        />
      )}

      {aliasForm && aliasCenterCode && (
        <AliasModal
          value={aliasForm}
          center={centers.find((item) => item.centerCode === aliasCenterCode)}
          editing={Boolean(editingAliasId)}
          sourceOptions={sourceOptions}
          onChange={setAliasForm}
          onSave={() => void saveAlias()}
          onClose={() => { setAliasForm(null); setAliasCenterCode(null); setEditingAliasId(null); }}
        />
      )}

      {confirmation && (
        <AdminConfirmModal
          title={`${confirmation.activate ? 'Activate' : 'Deactivate'} ${confirmation.kind === 'center' ? 'Centre' : 'Alias'}?`}
          message={confirmation.kind === 'center'
            ? `${confirmation.activate ? 'Activate' : 'Deactivate'} ${confirmation.center.displayName}?`
            : `${confirmation.activate ? 'Activate' : 'Deactivate'} ${confirmation.alias.rawCenterValue} for ${confirmation.center.displayName}?`}
          onConfirm={() => void confirmStatusChange()}
          onCancel={() => setConfirmation(null)}
        />
      )}

      {alert && (
        <AdminAlertModal
          title={alert.title}
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
    </section>
  );
}

function CenterRows({
  center,
  expanded,
  onToggle,
  onEdit,
  onAddAlias,
  onEditAlias,
  onStatus,
  onAliasStatus,
}: {
  center: BusinessReportCenter;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onAddAlias: () => void;
  onEditAlias: (alias: BusinessReportCenterAlias) => void;
  onStatus: (activate: boolean) => void;
  onAliasStatus: (alias: BusinessReportCenterAlias, activate: boolean) => void;
}) {
  return (
    <>
      <tr>
        <td><strong>#{center.displayOrder}</strong><span className="business-source-secondary">{center.centerCode}</span></td>
        <td><strong>{center.displayName}</strong><span className="business-source-secondary">Updated {dateTime(center.updatedAt)}</span></td>
        <td>{center.accessCenterName}</td>
        <td>
          <span>Launch: {dateOnly(center.launchDate) || 'Not set'}</span>
          <span className="business-source-secondary">Effective: {dateRange(center.effectiveFrom, center.effectiveTo)}</span>
        </td>
        <td><Status value={center.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
        <td>
          <button className="btn btn-outline btn-sm" onClick={onToggle}>
            {center.aliases.length} {center.aliases.length === 1 ? 'alias' : 'aliases'} {expanded ? '▲' : '▼'}
          </button>
        </td>
        <td>
          <div className="business-source-actions">
            <button className="business-centre-action is-edit" onClick={onEdit}>Edit</button>
            <button className="business-centre-action is-alias" onClick={onAddAlias}>Add Alias</button>
            <button className={`business-centre-action ${center.isActive ? 'is-deactivate' : 'is-activate'}`} onClick={() => onStatus(!center.isActive)}>
              {center.isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7}>
            <div className="enterprise-card" style={{ margin: 8, boxShadow: 'none' }}>
              <div className="enterprise-card-heading">
                <div><strong>Approved Source Aliases</strong><span>Whitespace and case normalize to the value shown under Normalized.</span></div>
                <button className="btn btn-primary btn-sm" onClick={onAddAlias}>+ Add Alias</button>
              </div>
              <div className="enterprise-table-wrap">
                <table className="enterprise-table">
                  <thead><tr><th>Namespace</th><th>Source Value</th><th>Normalized</th><th>Source</th><th>Effective</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {center.aliases.map((alias) => (
                      <tr key={alias.id}>
                        <td><strong>{alias.sourceNamespace}</strong></td>
                        <td>{alias.rawCenterValue}</td>
                        <td>{alias.normalizedAlias}</td>
                        <td>{alias.dataSource?.sourceName || 'Provider-wide'}</td>
                        <td>{dateRange(alias.effectiveFrom, alias.effectiveTo)}</td>
                        <td><Status value={alias.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
                        <td>
                          <div className="business-source-actions">
                            <button className="btn btn-outline btn-sm" onClick={() => onEditAlias(alias)}>Edit</button>
                            <button className="btn btn-outline btn-sm" onClick={() => onAliasStatus(alias, !alias.isActive)}>
                              {alias.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!center.aliases.length && <tr><td colSpan={7}>No aliases configured.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function CenterModal({
  value,
  editing,
  accessOptions,
  onChange,
  onSave,
  onClose,
}: {
  value: CenterForm;
  editing: boolean;
  accessOptions: BusinessReportAccessCenterOption[];
  onChange: (value: CenterForm) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const set = <K extends keyof CenterForm>(key: K, next: CenterForm[K]) => onChange({ ...value, [key]: next });
  return (
    <div className="business-source-modal-overlay">
      <div className="business-source-modal">
        <div className="business-source-modal-header">
          <div>
            <p className="eyebrow">Governed Clinical Location</p>
            <h3>{editing ? 'Edit Centre' : 'Add Centre'}</h3>
            <span>Management is an access identity and cannot be selected as a location.</span>
          </div>
          <button className="admin-modal-close" onClick={onClose} aria-label="Close centre form">&times;</button>
        </div>
        <div className="business-source-form-grid">
          <Field label="Centre Code" help="Stable identifier. It cannot be changed after creation.">
            <input value={value.centerCode} disabled={editing} onChange={(event) => set('centerCode', event.target.value.toUpperCase())} />
          </Field>
          <Field label="Display Name" help="Leading, trailing and repeated spaces are normalized.">
            <input value={value.displayName} onChange={(event) => set('displayName', event.target.value)} />
          </Field>
          <Field label="Access-centre Mapping">
            <select value={value.accessCenterName} onChange={(event) => set('accessCenterName', event.target.value)}>
              <option value="">Select clinical access centre</option>
              {accessOptions.map((option) => <option key={option.id} value={option.name}>{option.name}</option>)}
            </select>
          </Field>
          <Field label="Display Order">
            <input type="number" min="0" max="10000" value={value.displayOrder} onChange={(event) => set('displayOrder', event.target.value)} />
          </Field>
          <Field label="Launch Date" help="Optional; it can be added later.">
            <input type="date" value={value.launchDate} onChange={(event) => set('launchDate', event.target.value)} />
          </Field>
          <Field label="Effective From">
            <input type="date" value={value.effectiveFrom} onChange={(event) => set('effectiveFrom', event.target.value)} />
          </Field>
          <Field label="Effective To">
            <input type="date" value={value.effectiveTo} onChange={(event) => set('effectiveTo', event.target.value)} />
          </Field>
        </div>
        <div className="business-source-modal-footer">
          <span>All changes are normalized and written to the audit log.</span>
          <div><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={onSave}>Save Centre</button></div>
        </div>
      </div>
    </div>
  );
}

function AliasModal({
  value,
  center,
  editing,
  sourceOptions,
  onChange,
  onSave,
  onClose,
}: {
  value: AliasForm;
  center?: BusinessReportCenter;
  editing: boolean;
  sourceOptions: BusinessReportCenterSourceOption[];
  onChange: (value: AliasForm) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const set = <K extends keyof AliasForm>(key: K, next: AliasForm[K]) => onChange({ ...value, [key]: next });
  return (
    <div className="business-source-modal-overlay">
      <div className="business-source-modal">
        <div className="business-source-modal-header">
          <div>
            <p className="eyebrow">Source Alias</p>
            <h3>{editing ? 'Edit Alias' : 'Add Alias'}</h3>
            <span>{center?.displayName}. Unknown spellings are never guessed.</span>
          </div>
          <button className="admin-modal-close" onClick={onClose} aria-label="Close alias form">&times;</button>
        </div>
        <div className="business-source-form-grid">
          <Field label="Source Namespace" help="For example LOOKER, MEDICS or a governed source key.">
            <input value={value.sourceNamespace} onChange={(event) => set('sourceNamespace', event.target.value.toUpperCase())} />
          </Field>
          <Field label="Source Centre Value" help="Exact source spelling. Whitespace and case are normalized for matching.">
            <input value={value.rawCenterValue} onChange={(event) => set('rawCenterValue', event.target.value)} />
          </Field>
          <Field label="Governed Data Source" help="Optional; leave blank for a provider-wide alias.">
            <select value={value.dataSourceId} onChange={(event) => set('dataSourceId', event.target.value)}>
              <option value="">Provider-wide alias</option>
              {sourceOptions.map((source) => <option key={source.id} value={source.id}>{source.sourceName} ({source.sourceKey})</option>)}
            </select>
          </Field>
          <Field label="Mapping Owner">
            <input value={value.mappingOwner} onChange={(event) => set('mappingOwner', event.target.value)} />
          </Field>
          <Field label="Effective From">
            <input type="date" value={value.effectiveFrom} onChange={(event) => set('effectiveFrom', event.target.value)} />
          </Field>
          <Field label="Effective To">
            <input type="date" value={value.effectiveTo} onChange={(event) => set('effectiveTo', event.target.value)} />
          </Field>
          <label className="business-source-field">
            <span>Active after save</span>
            <input type="checkbox" checked={value.isActive} onChange={(event) => set('isActive', event.target.checked)} />
            <small>Active aliases can be used by approved adapters immediately.</small>
          </label>
        </div>
        <div className="business-source-modal-footer">
          <span>Misspellings require explicit aliases; fuzzy matching is not used.</span>
          <div><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={onSave}>Save Alias</button></div>
        </div>
      </div>
    </div>
  );
}

function centerInput(form: CenterForm): BusinessReportCenterInput {
  const displayOrder = Number(form.displayOrder);
  if (!form.centerCode.trim() || !form.displayName.trim() || !form.accessCenterName.trim()) {
    throw new Error('Centre code, display name and access-centre mapping are required.');
  }
  if (!Number.isInteger(displayOrder) || displayOrder < 0) throw new Error('Display order must be a non-negative whole number.');
  return {
    centerCode: form.centerCode.trim().toUpperCase(),
    displayName: cleanSpaces(form.displayName),
    accessCenterName: cleanSpaces(form.accessCenterName),
    displayOrder,
    launchDate: form.launchDate || null,
    effectiveFrom: form.effectiveFrom || null,
    effectiveTo: form.effectiveTo || null,
  };
}

function aliasInput(form: AliasForm): BusinessReportCenterAliasInput {
  if (!form.sourceNamespace.trim() || !form.rawCenterValue.trim()) {
    throw new Error('Source namespace and source centre value are required.');
  }
  return {
    sourceNamespace: form.sourceNamespace.trim().toUpperCase(),
    rawCenterValue: cleanSpaces(form.rawCenterValue),
    dataSourceId: form.dataSourceId || null,
    mappingOwner: cleanSpaces(form.mappingOwner) || null,
    effectiveFrom: form.effectiveFrom || null,
    effectiveTo: form.effectiveTo || null,
    isActive: form.isActive,
  };
}

function cleanSpaces(value: string) {
  return value.trim().replace(/\s+/g, ' ');
}

function Field({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return <label className="business-source-field"><span>{label}</span>{children}{help && <small>{help}</small>}</label>;
}

function Kpi({ label, value, tone }: { label: string; value: number; tone: string }) {
  return <div className={`business-source-kpi ${tone}`}><span>{label}</span><strong>{value}</strong></div>;
}

function Status({ value }: { value: string }) {
  return <span className={`business-source-status ${value.toLowerCase()}`}>{value}</span>;
}

function dateOnly(value: string | null) {
  return value ? value.slice(0, 10) : '';
}

function dateRange(from: string | null, to: string | null) {
  return `${dateOnly(from) || 'Open'} → ${dateOnly(to) || 'Open'}`;
}

function dateTime(value: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString('en-IN');
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}
